/**
 * Servicio de sincronización en tiempo real vía WebSocket
 * 
 * Este servicio maneja la conexión WebSocket entre el frontend y el backend
 * para recibir notificaciones de cambios en datos en tiempo real.
 * 
 * Características:
 * - Conexión automática al servidor WebSocket
 * - Registro como cliente con información de tenant
 * - Sistema de suscripción para que componentes escuchen eventos específicos
 * - Reconexión automática en caso de desconexión
 * - Filtrado por tenant (solo recibe notificaciones de su tenant)
 * 
 * Uso:
 * ```javascript
 * import websocketSyncService from './services/websocketSyncService';
 * 
 * // Suscribirse a actualizaciones de cuenta corriente
 * const unsubscribe = websocketSyncService.subscribe('cuentaCorriente:updated', (data) => {
 *   console.log('Cuenta corriente actualizada:', data);
 * });
 * 
 * // Desuscribirse cuando el componente se desmonte
 * return () => unsubscribe();
 * ```
 */

import Cookies from 'js-cookie';

class WebSocketSyncService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.listeners = new Map(); // Almacenar suscriptores por tipo de evento
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.reconnectTimeout = null;
  }

  /**
   * Obtiene la URL del servidor WebSocket según el entorno
   */
  getServerURL() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = process.env.REACT_APP_WS_PORT || '5000';
    
    // En desarrollo local
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${protocol}//${host}:${port}`;
    }
    
    // En producción
    return `${protocol}//${host}`;
  }

  /**
   * Obtiene el tenant desde las cookies
   */
  getTenantFromCookies() {
    return Cookies.get('tenantURL') || Cookies.get('tenant') || '';
  }

  /**
   * Obtiene el usuario desde las cookies
   */
  getUserFromCookies() {
    return Cookies.get('usuario') || '';
  }

  /**
   * Conecta al servidor WebSocket
   */
  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('⚠️ Ya existe una conexión WebSocket activa');
      return;
    }

    try {
      const serverURL = this.getServerURL();
      console.log(`🔌 Conectando al WebSocket de sincronización: ${serverURL}`);
      
      this.ws = new WebSocket(serverURL);

      this.ws.onopen = () => {
        console.log('✅ Conectado al servidor WebSocket de sincronización');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.registerAsClient();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('❌ Error procesando mensaje WebSocket:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('🔌 Desconectado del servidor WebSocket de sincronización');
        this.isConnected = false;
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('❌ Error de conexión WebSocket:', error);
        this.isConnected = false;
      };

    } catch (error) {
      console.error('❌ Error al conectar WebSocket:', error);
      this.attemptReconnect();
    }
  }

  /**
   * Registra el cliente en el servidor con información de tenant
   */
  registerAsClient() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const registrationMessage = {
        type: 'register_client',
        data: {
          userAgent: navigator.userAgent,
          tenant: this.getTenantFromCookies(),
          usuario: this.getUserFromCookies(),
          purpose: 'data_sync' // Identificar que es para sincronización de datos
        }
      };
      
      this.ws.send(JSON.stringify(registrationMessage));
      console.log('📝 Cliente registrado para sincronización de datos');
    }
  }

  /**
   * Maneja mensajes recibidos del servidor
   */
  handleMessage(message) {
    const { type, data } = message;

    // Manejar mensajes del sistema
    if (type === 'connection_established') {
      console.log('🎉 Conexión establecida:', data.message);
      return;
    }

    if (type === 'registration_success') {
      console.log('✅ Registro exitoso:', data.message);
      return;
    }

    // Manejar notificaciones de actualización de datos
    if (type === 'data_update') {
      this.handleDataUpdate(data);
      return;
    }

    // Mensaje desconocido
    console.log('📨 Mensaje WebSocket recibido:', type, data);
  }

  /**
   * Maneja notificaciones de actualización de datos
   */
  handleDataUpdate(data) {
    const { module, action } = data;
    
    if (!module || !action) {
      console.warn('⚠️ Notificación de actualización sin module o action:', data);
      return;
    }

    // Construir el nombre del evento: "module:action" (ej: "cuentaCorriente:updated")
    const eventName = `${module}:${action}`;
    
    console.log(`🔔 Actualización recibida: ${eventName}`, data);

    // Notificar a todos los suscriptores de este evento
    const handlers = this.listeners.get(eventName) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`❌ Error en handler de ${eventName}:`, error);
      }
    });
  }

  /**
   * Intenta reconectar al servidor
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Máximo de intentos de reconexión alcanzado');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * this.reconnectAttempts;
    
    console.log(`🔄 Intentando reconectar en ${delay}ms (intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Suscribe un callback a un evento específico
   * @param {string} eventName - Nombre del evento (ej: "cuentaCorriente:updated")
   * @param {Function} callback - Función a ejecutar cuando ocurra el evento
   * @returns {Function} Función para desuscribirse
   */
  subscribe(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    
    this.listeners.get(eventName).push(callback);
    console.log(`📌 Suscrito a evento: ${eventName}`);

    // Retornar función para desuscribirse
    return () => {
      const handlers = this.listeners.get(eventName);
      if (handlers) {
        const index = handlers.indexOf(callback);
        if (index > -1) {
          handlers.splice(index, 1);
          console.log(`📍 Desuscrito de evento: ${eventName}`);
        }
      }
    };
  }

  /**
   * Desconecta del servidor WebSocket
   */
  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.listeners.clear();
    console.log('🔌 Desconectado del servidor WebSocket de sincronización');
  }

  /**
   * Obtiene el estado de la conexión
   */
  getConnectionState() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      hasListeners: this.listeners.size > 0
    };
  }
}

// Crear instancia singleton
const websocketSyncService = new WebSocketSyncService();

// Conectar automáticamente al cargar
if (typeof window !== 'undefined') {
  websocketSyncService.connect();
}

export default websocketSyncService;

